import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';
import { ConfigService } from '../../core/config/config.service';
import { NavigationModel } from './navigation.model';
import { NavigationService } from './navigation.service';

@Component({
	selector: 'app-navigation',
	templateUrl: './navigation.component.html',
	standalone: false
})
export class NavigationComponent implements OnInit {
	entries: NavigationModel[] = [];
	@Output() onNavigationToggle = new EventEmitter<void>();
	toShowWireless: boolean | null = null;

	private destroy$ = new Subject<void>();

	public GROUP = {
		DASHBOARD: 'Dashboard',
		ADMIN: 'Admin',
		COST_ANALYSIS: 'Cost Analysis',
		APP_DATA: 'App Data',
		CONTRACTS: 'Contracts',
		GENERAL_LEDGER: 'GL',
		INVENTORY: 'Inventory',
		ORDERS: 'Orders',
		AUDIT: 'Audit',
		QUOTES: 'Quotes',
		WORK_MANAGEMENT: 'Work Management',
		USAGE_MANAGEMENT: 'Usage Management',
		REPORTING: 'Reports & Analysis',
		WIRELESS: 'Wireless'
	};

	constructor(
		public navigationService: NavigationService,
		public router: Router,
		public configService: ConfigService
	) {}

	loadConfig(): Observable<any> {
		return this.configService.findAll();
	}

	ngOnInit() {
		// Load config and then navigation entries
		this.loadConfig()
			.pipe(
				mergeMap((res) => {
					this.toShowWireless = !!res?.show_wireless;
					return this.navigationService.load();
				}),
				takeUntil(this.destroy$)
			)
			.subscribe((entries) => {
				this.entries = this.toShowWireless ? entries : this.filterWirelessEntries(entries || []);
				this.navActivator();
			});

		// React to route changes
		this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.navActivator();
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	navActivator() {
		const route = this.getBaseRoute();
		this.deselectItems();

		for (const entry of this.entries) {
			if (entry.value === route) {
				this.setLastClicked(entry);
				entry.selected = true;
			}

			if (entry.hasItems()) {
				for (const item of entry.items) {
					if (item.value === route) {
						entry.open = true;
						this.setLastClicked(entry);
						item.selected = true;
					}
				}
			}
		}
	}

	private getBaseRoute(): string {
		const match = window.location.pathname.match(/^\/[^\/]+/);
		return match ? match[0] : '/';
	}

	getVisibleEntries(entries: NavigationModel[] = []): NavigationModel[] {
		return entries.filter((x) => x.visible); // && x.allowed -- TODO: Put back in maybe if we want visible and disabled
	}

	toggle(entry: NavigationModel) {
		entry.open = !entry.open;
		this.onNavigationToggle.emit();
		this.setLastClicked(entry);
	}

	closeAll() {
		this.navigationService.closeAll();
	}

	isSelected(entry: NavigationModel) {
		return entry.items ? entry.items.some((item) => item.selected) : !!entry.selected;
	}

	deselectItems() {
		this.entries?.forEach((entry) => {
			entry.selected = false;
			if (entry.items) {
				for (const item of entry.items) {
					item.selected = false;
				}
			}
		});
	}

	setLastClicked(entry?: NavigationModel) {
		for (const e of this.entries) {
			e.focused = false;
		}

		if (entry) {
			entry.focused = true;
		}
	}

	private filterWirelessEntries(entries: NavigationModel[]): NavigationModel[] {
		return entries
			.map((entry) => {
				if (entry && entry.items) {
					const filteredItems = entry.items.filter((i: any) => i && i.group !== this.GROUP.WIRELESS);
					entry.items = filteredItems;
					return entry;
				}

				return entry;
			})
			.filter(Boolean);
	}
}
